import { usePreferenceCenter } from '@church/data'
import {
  faBell,
  faBellSlash,
  faEnvelope,
  faLinkSlash,
  faMessage,
  type IconData,
} from '@church/ui-icons'
import { createFileRoute } from '@tanstack/react-router'
import { Card, ErrorNote, Icon, Spinner } from '../components/ui.tsx'

/**
 * The preference center, from the link in every email. No account: the
 * link's token is the credential, and it only ever shows this one person's
 * choices — never their name, address or anything else.
 */
export const Route = createFileRoute('/preferences/$ref')({
  component: PreferencesPage,
})

function PreferencesPage() {
  const { ref } = Route.useParams()
  const { preferences, update } = usePreferenceCenter(ref)
  const p = preferences.data
  return (
    <div className="min-h-screen bg-surface-base px-4 py-12">
      <Card className="mx-auto max-w-md space-y-5 p-6">
        {preferences.isPending && <Spinner />}
        {preferences.error && (
          <p className="flex items-center gap-2 text-content-default">
            <Icon icon={faLinkSlash} className="size-5 text-content-subtle" />
            This link has expired or is not valid.
          </p>
        )}
        {p && (
          <>
            <div>
              <h1 className="text-xl font-bold text-content-default">{p.churchName}</h1>
              <p className="text-sm text-content-muted">Choose how we keep in touch.</p>
            </div>
            <Toggle
              label="Emails"
              icon={faEnvelope}
              checked={p.all && p.email}
              disabled={!p.all}
              onChange={(email) => update.mutate({ email })}
            />
            <Toggle
              label="Text messages"
              icon={faMessage}
              checked={p.all && p.sms}
              disabled={!p.all || p.smsLocked}
              hint={
                p.smsLocked ? 'You texted STOP. Text START to us to turn texts back on.' : undefined
              }
              onChange={(sms) => update.mutate({ sms })}
            />
            <div className="border-t border-border-subtle pt-4">
              {p.all ? (
                <button
                  className="inline-flex items-center gap-1.5 text-sm text-danger-solid"
                  onClick={() => update.mutate({ all: false })}
                >
                  <Icon icon={faBellSlash} className="size-3.5" />
                  Unsubscribe from everything
                </button>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-content-default">
                    You’re unsubscribed from everything.
                  </p>
                  <button
                    className="inline-flex items-center gap-1.5 text-sm text-content-brand"
                    onClick={() => update.mutate({ all: true })}
                  >
                    <Icon icon={faBell} className="size-3.5" />
                    Resubscribe
                  </button>
                </div>
              )}
            </div>
            <ErrorNote error={update.error} />
          </>
        )}
      </Card>
    </div>
  )
}

function Toggle({
  label,
  icon,
  checked,
  disabled,
  hint,
  onChange,
}: {
  label: string
  icon: IconData
  checked: boolean
  disabled?: boolean
  hint?: string
  onChange: (value: boolean) => void
}) {
  return (
    <label className={`flex items-start justify-between gap-4 ${disabled ? 'opacity-60' : ''}`}>
      <span className="flex gap-3">
        <Icon icon={icon} className="mt-1 size-4 text-content-muted" />
        <span>
          <span className="block font-medium text-content-default">{label}</span>
          {hint && <span className="block text-sm text-content-muted">{hint}</span>}
        </span>
      </span>
      <input
        type="checkbox"
        role="switch"
        className="mt-1 size-5 accent-brand-solid"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  )
}
